import React, { Component } from "react";
import "./ErrorBoundary.css";

/**
 * Catches render/lifecycle errors below it so a bad API payload degrades into a
 * message instead of a blank page. The app had no boundary at all, so any throw —
 * e.g. a resource whose endpoint replies with an HTML error page instead of JSON —
 * unmounted everything.
 *
 * Reducer errors are NOT caught by this (they happen inside dispatch, not render),
 * so guards still belong in the slices; this is the last line of defence.
 */
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Kept as a console error so the stack is still reachable in production logs.
    console.error("Unhandled UI error:", error, errorInfo);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div className="err-boundary">
        <div className="err-boundary-card">
          <div className="err-boundary-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" width="28" height="28">
              <path d="M12 9v4" strokeLinecap="round" />
              <path d="M12 16.5v.5" strokeLinecap="round" />
              <path d="M10.3 3.9L2.6 17.2A1.9 1.9 0 004.3 20h15.4a1.9 1.9 0 001.7-2.8L13.7 3.9a1.9 1.9 0 00-3.4 0z" />
            </svg>
          </div>

          <h3 className="err-boundary-title">{this.props.title || "Something went wrong"}</h3>
          <p className="err-boundary-text">
            {this.props.message ||
              "We could not display this section. This is usually caused by unexpected data coming back from the server."}
          </p>

          <div className="err-boundary-actions">
            <button className="err-boundary-btn" onClick={this.handleRetry}>
              Try again
            </button>
            <button className="err-boundary-btn err-boundary-btn-primary" onClick={() => window.location.reload()}>
              Reload page
            </button>
          </div>

          {this.state.error ? (
            <details className="err-boundary-details">
              <summary>Technical details</summary>
              <pre>{String(this.state.error && (this.state.error.stack || this.state.error.message))}</pre>
            </details>
          ) : null}
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
